import React, { useState, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import {
  Box,
  TextField,
  Typography,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined"
import IntegerCoefficientFormat from "common/IntegerCoefficientFormat"

import {
  DEFAULT_DECIMALS,
  UNITS,
  TOTALS,
  MARGINS,
  REDUCTIONS,
  MEASUREMENTS,
  DIMENSIONS,
  SUB_DECOMPOSITIONS,
  TOTAL_DECOMPOSITIONS,
  COEFFICIENT_DECIMALS_CAP,
} from "constants/decimals"

export default function DecimalsTab({
  localProject,
  setLocalProject,
  setDecimalsTouched,
}) {
  const [locked, setLocked] = useState(true)

  const handleChange = field => e => {
    if (typeof setDecimalsTouched === "function") setDecimalsTouched(true)
    const value = e.target.value === "" ? "" : Number(e.target.value)
    setLocalProject(lp => ({
      ...lp,
      coefficients: {
        ...lp.coefficients,
        [field]: value,
      },
    }))
  }

  const handleBlur = field => () => {
    setLocalProject(lp => {
      const curr = lp?.coefficients?.[field]
      if (curr === "" || curr === undefined || curr === null) {
        return {
          ...lp,
          coefficients: {
            ...lp.coefficients,
            [field]: 0,
          },
        }
      }
      if (typeof curr === "number" && curr > COEFFICIENT_DECIMALS_CAP) {
        return {
          ...lp,
          coefficients: {
            ...lp.coefficients,
            [field]: COEFFICIENT_DECIMALS_CAP,
          },
        }
      }
      return lp
    })
  }

  const fields = useMemo(
    () => [
      {
        key: "units",
        label: i18n.t("editProject.units"),
        default: DEFAULT_DECIMALS[UNITS],
      },
      {
        key: "totals",
        label: i18n.t("editProject.totals"),
        default: DEFAULT_DECIMALS[TOTALS],
      },
      {
        key: "margins",
        label: i18n.t("editProject.margins"),
        default: DEFAULT_DECIMALS[MARGINS],
      },
      {
        key: "reductions",
        label: i18n.t("editProject.reductions"),
        default: DEFAULT_DECIMALS[REDUCTIONS],
      },
      {
        key: "measurements",
        label: i18n.t("editProject.measurements"),
        default: DEFAULT_DECIMALS[MEASUREMENTS],
      },
      {
        key: "dimensions",
        label: i18n.t("editProject.dimensions"),
        default: DEFAULT_DECIMALS[DIMENSIONS],
      },
      {
        key: "sub_decompositions",
        label: i18n.t("editProject.subDecompositions"),
        default: DEFAULT_DECIMALS[SUB_DECOMPOSITIONS],
      },
      {
        key: "total_decompositions",
        label: i18n.t("editProject.totalDecompositions"),
        default: DEFAULT_DECIMALS[TOTAL_DECOMPOSITIONS],
      },
    ],
    [],
  )

  useEffect(() => {
    setLocalProject(lp => {
      const coeffs = lp?.coefficients || {}
      let changed = false
      const newCoeffs = { ...coeffs }
      fields.forEach(f => {
        if (newCoeffs[f.key] === undefined || newCoeffs[f.key] === null) {
          newCoeffs[f.key] = f.default
          changed = true
        }
      })
      if (!changed) return lp
      return {
        ...lp,
        coefficients: newCoeffs,
      }
    })
  }, [fields, setLocalProject])

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {i18n.t("editProject.decimalEditionInfo")}
        </Typography>
        <Tooltip
          title={
            locked
              ? i18n.t("editProject.unblockEdition")
              : i18n.t("editProject.blockEdition")
          }
        >
          <IconButton
            aria-label={
              locked
                ? i18n.t("editProject.unblockEdition")
                : i18n.t("editProject.blockEdition")
            }
            onClick={() => setLocked(prev => !prev)}
            size="small"
          >
            {locked ? (
              <LockOutlinedIcon fontSize="small" />
            ) : (
              <LockOpenOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2}>
        {fields.map(f => (
          <Grid item xs={6} sm={4} md={4} key={f.key}>
            <TextField
              label={f.label}
              fullWidth
              variant="outlined"
              size="small"
              name={f.key}
              value={localProject.coefficients?.[f.key] ?? f.default}
              margin="normal"
              InputProps={{ inputComponent: IntegerCoefficientFormat }}
              error={
                typeof localProject.coefficients?.[f.key] === "number" &&
                localProject.coefficients[f.key] > COEFFICIENT_DECIMALS_CAP
              }
              helperText={
                typeof localProject.coefficients?.[f.key] === "number" &&
                localProject.coefficients[f.key] > COEFFICIENT_DECIMALS_CAP
                  ? `${i18n.t("numerationSettings.max")} ${COEFFICIENT_DECIMALS_CAP}`
                  : undefined
              }
              onChange={handleChange(f.key)}
              onBlur={handleBlur(f.key)}
              disabled={locked}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

DecimalsTab.propTypes = {
  localProject: PropTypes.object.isRequired,
  setLocalProject: PropTypes.func.isRequired,
  setDecimalsTouched: PropTypes.func,
}
